// v14at — Compose-dialog voor "Offerte gegenereerd → E-mail".
//
// Doel: repareert de bestaande flow zodat de E-mail-knop in de "Offerte
// gegenereerd"-modal daadwerkelijk verstuurt via de reeds gekoppelde
// SMTP-mailbox (endpoint /api/quotes/{qid}/send-email), met:
//   • defaults uit user.email_settings (subject/body/signature)
//   • placeholder-invulling ({project_name}, {quote_number}, {client_name})
//   • verse PDF als bijlage (backend genereert de PDF opnieuw)
//   • duidelijke user-feedback (toast + inline error)
//   • fallback naar mailto: wanneer geen mailbox gekoppeld is
//
// Geen nieuw mail-provider-architectuur — hergebruikt exact dezelfde
// endpoints en helper-logic als de bestaande CoverEmailPanel-flow.
import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Send, RefreshCw, ExternalLink, AlertCircle, Mail } from "lucide-react";
import { toast } from "sonner";
import { useQuoteEmailSend, EmailSendPrompts, EmailDispatchHistory, SmtpStatusHints } from "./emailSend";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function _fillPlaceholders(text, vars) {
  return (text || "").replace(/\{(project_name|quote_number|client_name)\}/g,
    (_, k) => (vars[k] ?? "").toString());
}

function _appendSignatureOnce(body, signature, vars) {
  const sig = _fillPlaceholders(signature || "", vars).trim();
  if (!sig) return (body || "").replace(/\s+$/g, "");
  const bodyClean = (body || "").replace(/\s+$/g, "");
  if (!bodyClean) return sig;
  const norm = (s) => (s || "").replace(/\s+/g, " ").trim().toLowerCase();
  const tail = norm(bodyClean.slice(-800));
  if (tail.includes(norm(sig))) return bodyClean;
  return `${bodyClean}\n\n${sig}`;
}

function _buildDefaultBody(emailSettings, vars) {
  const body = _fillPlaceholders(emailSettings.default_body || "", vars);
  return _appendSignatureOnce(body, emailSettings.signature || "", vars);
}

function _buildDefaultSubject(emailSettings, vars) {
  return _fillPlaceholders(emailSettings.default_subject || "", vars);
}

/**
 * @param open           — controle van dialog-visibility
 * @param onClose        — sluit callback
 * @param quoteId        — quote.id (voor /quotes/{id}/send-email)
 * @param quoteNumber    — quote_number (placeholder + subject default)
 * @param quoteUrl       — inline PDF-URL (voor mailto-fallback)
 * @param projectName    — project.name
 * @param clientName     — client.name
 * @param clientEmail    — voorgestelde ontvanger
 */
export default function QuoteEmailComposeDialog({
  open, onClose,
  quoteId, quoteNumber = "", quoteUrl = "",
  projectName = "", clientName = "", clientEmail = "", onQuoteStatusChange,
}) {
  const [loaded, setLoaded] = useState(false);
  const [smtpConnected, setSmtpConnected] = useState(false);
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [smtpCfg, setSmtpCfg] = useState({});
  const [err, setErr] = useState("");
  const refreshSmtp = () => fetch(`${API}/auth/me`, { credentials: "include" })
    .then((r) => (r.ok ? r.json() : {})).then((me) => setSmtpCfg(me.smtp_config || {})).catch(() => {});
  const ctl = useQuoteEmailSend(open ? quoteId : null, { onQuoteStatusChange, onFailed: refreshSmtp });
  const sending = ctl.sending;

  const vars = useMemo(() => ({
    project_name: projectName, quote_number: quoteNumber, client_name: clientName,
  }), [projectName, quoteNumber, clientName]);

  useEffect(() => {
    if (!open) return;
    setErr("");
    setLoaded(false);
    (async () => {
      try {
        const r = await fetch(`${API}/auth/me`, { credentials: "include" });
        const me = r.ok ? await r.json() : {};
        const s = me.email_settings || {};
        setSmtpConnected(!!(me.smtp_config || {}).connected);
        setSmtpCfg(me.smtp_config || {});
        setSubject(_buildDefaultSubject(s, vars) || `Offerte ${quoteNumber} — ${projectName}`);
        setBody(_buildDefaultBody(s, vars) || "Beste,\n\nIn bijlage vindt u onze offerte.\n\nMet vriendelijke groet.");
        setTo(clientEmail || "");
      } catch {
        setSubject(`Offerte ${quoteNumber} — ${projectName}`);
        setBody("Beste,\n\nIn bijlage vindt u onze offerte.\n\nMet vriendelijke groet.");
        setTo(clientEmail || "");
      } finally {
        setLoaded(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, quoteId, clientEmail]);

  const doSend = async (confirmUnknown = false) => {
    setErr("");
    const recipient = (to || "").trim();
    if (!recipient) {
      const msg = "Vul een ontvanger-e-mailadres in.";
      setErr(msg); toast.error(msg);
      return;
    }
    const res = await ctl.send({ to: recipient, subject, body, confirmUnknown });
    if (res.cancelled) return;
    if (res.ok) {
      toast.success(`Aanvaard door je mailserver (naar ${recipient}, bijlage: ${res.attachment || "PDF"}). Aflevering bij de klant is niet bevestigd.`);
    } else if (res.message) {
      setErr(res.message); toast.error(res.message);
    }
  };

  const doMailtoFallback = () => {
    const href = `mailto:${encodeURIComponent(to || "")}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(
      `${body}\n\n— Direct openen: ${quoteUrl || ""}`
    )}`;
    window.location.href = href;
    onClose && onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="quote-email-compose-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#D96C4A]" /> E-mail verzenden
          </DialogTitle>
          <DialogDescription>
            Offerte <span className="font-mono">{quoteNumber}</span>
            {projectName ? ` — ${projectName}` : ""}
          </DialogDescription>
        </DialogHeader>

        {!loaded ? (
          <div className="py-8 text-center text-sm text-[#6E6E6C]">Laden…</div>
        ) : (
          <div className="space-y-4">
            {!smtpConnected && (
              <div className="text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-2"
                data-testid="quote-email-no-smtp-hint">
                <AlertCircle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
                <div className="flex-1 text-amber-900">
                  Geen mailbox gekoppeld. Koppel je mailbox in <strong>Instellingen → E-mail</strong> om
                  rechtstreeks te versturen (met PDF als bijlage). Je kan nu wel openen in je eigen mailprogramma.
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="qec-to" className="text-xs font-medium">Naar</Label>
              <Input
                id="qec-to"
                type="email"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                placeholder="klant@example.com"
                className="text-sm"
                data-testid="quote-email-to"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="qec-subject" className="text-xs font-medium">Onderwerp</Label>
              <Input
                id="qec-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Onderwerpregel"
                className="text-sm"
                data-testid="quote-email-subject"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="qec-body" className="text-xs font-medium">Bericht</Label>
              <Textarea
                id="qec-body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={9}
                placeholder="Begeleidend bericht"
                className="text-sm font-sans"
                data-testid="quote-email-body"
              />
            </div>

            {smtpConnected && (
              <div className="text-[11px] text-[#5D7259] bg-[#FAF9F6] border border-[#F0EFEA] rounded-lg px-3 py-2">
                De actuele PDF van deze offerte wordt automatisch als bijlage meegestuurd via je gekoppelde mailbox.
              </div>
            )}

            <SmtpStatusHints cfg={smtpCfg} prefix="quote-email" />
            {err && (
              <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
                data-testid="quote-email-error">
                {err}
              </div>
            )}
            <EmailSendPrompts ctl={ctl} onConfirmUnknown={() => doSend(true)} prefix="quote-email" />
            <EmailDispatchHistory rows={ctl.history} prefix="quote-email" />
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            className="h-11 rounded-xl"
            onClick={() => onClose && onClose()}
            data-testid="quote-email-cancel"
          >
            Annuleren
          </Button>
          {!smtpConnected ? (
            <Button
              onClick={doMailtoFallback}
              className="h-11 rounded-xl bg-[#5D7259] hover:bg-[#4a5c47] text-white gap-2"
              data-testid="quote-email-mailto-btn"
            >
              <ExternalLink className="w-4 h-4" /> Open in mijn mailprogramma
            </Button>
          ) : (
            <Button
              onClick={() => doSend(false)}
              disabled={sending || !loaded}
              className="h-11 rounded-xl bg-[#D96C4A] hover:bg-[#C05A3C] text-white gap-2 disabled:opacity-60"
              data-testid="quote-email-send-btn"
            >
              {sending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {sending ? "Verzenden…" : "Verzenden via mijn mailbox"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
